import { cloneDeep, isEmpty } from 'lodash'
import CrosstabDisplayType from 'enums/CrosstabDisplayType'
import ComparisonMode from 'enums/ComparisonMode'
import DisplayOption from 'enums/DisplayOption'
import CrosstabSortOption from 'enums/CrosstabSortOption'
import CrosstabDisplayTypeDetail from 'enums/CrosstabDisplayTypeDetail'
import messageUtil from 'utils/messageUtil'
import i18n from 'i18n'

import { CrosstabWidgetsType } from '../../types'
import { CALCULATED_OPTIONS, DUMMY_COLUMN_ID, ROW_GRAND_TOTAL_ID } from '../constants'

export const getWidgetSettingConfig = (widgetSetting: CrosstabWidgetsType) => ({
  sortAlphabetically: widgetSetting.ctSort === CrosstabSortOption.ALPHABETICALLY,
  secondarySort: widgetSetting.ctSort !== CrosstabSortOption.SELECTED_ORDER,
  displayColumn: widgetSetting.ctDisplayTotal === CrosstabDisplayTypeDetail.COLUMNS,
  showTable: widgetSetting.displayOption === DisplayOption.TABLE,
  displayAverage: CALCULATED_OPTIONS.includes(widgetSetting.viewOption!),
  displayRowGrandTotal:
    widgetSetting.ctDisplay === CrosstabDisplayType.PERCENT_SHARE &&
    widgetSetting.ctPercentShare === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL,
  displayRow: widgetSetting.ctDisplayTotal === CrosstabDisplayTypeDetail.ROWS,
  displayValue: widgetSetting.ctDisplay === CrosstabDisplayType.VALUE,
  displayShare: widgetSetting.ctDisplay === CrosstabDisplayType.PERCENT_SHARE
})

export const getSettingTopicInfos = (widgetSetting: CrosstabWidgetsType) => {
  if (widgetSetting.rowComparisonMode !== ComparisonMode.TOPICS) {
    return widgetSetting.selectedTopic ? [widgetSetting.selectedTopic] : []
  }
  return widgetSetting.topicInfos ? widgetSetting.topicInfos : []
}

export const sort = (a: string, b: string) => a.localeCompare(b)

export const sortByName = (a: { name?: string }, b: { name?: string }) => {
  const aName = a.name || ''
  const bName = b.name || ''
  return aName.localeCompare(bName)
}

export const getRowData = (widgetSetting: CrosstabWidgetsType) => {
  const {
    sortAlphabetically,
    secondarySort,
    displayColumn,
    showTable,
    displayRow,
    displayAverage
  } = getWidgetSettingConfig(widgetSetting)
  const topicInfos = cloneDeep(getSettingTopicInfos(widgetSetting))
  const rowThemeInfos = cloneDeep(widgetSetting.pivotRowThemeInfos)
  const rowTagNames = cloneDeep(widgetSetting.rowTagNames) as string[]
  const rowDocNames = cloneDeep(widgetSetting.rowDocNames) as string[]

  const rowIds: string[] = []
  const rowNames: string[] = []

  switch (widgetSetting.rowComparisonMode) {
    case ComparisonMode.TOPICS:
      if (sortAlphabetically || (secondarySort && displayColumn)) {
        topicInfos.sort(sortByName)
      }
      topicInfos.forEach((topicInfo) => {
        rowIds.unshift(topicInfo.topicId!.toString())
        rowNames.unshift(topicInfo.name!)
      })
      break
    case ComparisonMode.THEMES:
      if (sortAlphabetically || (secondarySort && displayColumn)) {
        rowThemeInfos!.sort(sortByName)
      }
      rowThemeInfos!.forEach((rowThemeInfo) => {
        rowIds.unshift(rowThemeInfo.themeId!.toString())
        rowNames.unshift(rowThemeInfo.name!)
      })
      break
    case ComparisonMode.TAGS:
      if (sortAlphabetically || (secondarySort && displayColumn)) {
        rowTagNames.sort(sort)
      }
      rowTagNames.forEach((rowTagName) => {
        rowIds.unshift(rowTagName)
        rowNames.unshift(rowTagName)
      })
      break
    case ComparisonMode.DOC_HIERARCHIES:
      if (sortAlphabetically || (secondarySort && displayColumn)) {
        rowDocNames.sort(sort)
      }
      rowDocNames.forEach((rowDocName) => {
        rowIds.unshift(rowDocName)
        rowNames.unshift(rowDocName)
      })
      break
    default:
      break
  }
  if (showTable && displayRow) {
    rowNames.unshift(displayAverage ? i18n.t('average') : i18n.t('total'))
  }

  return { rowIds, rowNames }
}

export const getColumnData = (widgetSetting: CrosstabWidgetsType, isAudienceAnalysis: boolean) => {
  const {
    sortAlphabetically,
    secondarySort,
    displayColumn,
    showTable,
    displayAverage,
    displayRowGrandTotal
  } = getWidgetSettingConfig(widgetSetting)
  const colThemeInfos = cloneDeep(widgetSetting.pivotThemeInfos)
  const colTagNames = cloneDeep(widgetSetting.tagNames) as string[]
  const colDocNames = cloneDeep(widgetSetting.docNames) as string[]

  const columnIds: string[] = []
  const columnNames: string[] = []

  switch (widgetSetting.colComparisonMode) {
    case ComparisonMode.THEMES:
      if (colThemeInfos && colThemeInfos.length > 0) {
        if (sortAlphabetically || (secondarySort && !displayColumn)) {
          colThemeInfos.sort(sortByName)
        }
        colThemeInfos.forEach((colThemeInfo) => {
          columnIds.push(colThemeInfo.themeId!.toString())
          columnNames.push(colThemeInfo.name!)
        })
      } else {
        columnIds.push(DUMMY_COLUMN_ID)
        columnNames.push(messageUtil.viewOption(widgetSetting.viewOption!))
      }
      break
    case ComparisonMode.TAGS:
      if (colTagNames && colTagNames.length > 0) {
        if (sortAlphabetically || (secondarySort && !displayColumn)) {
          colTagNames.sort(sort)
        }
        colTagNames.forEach((colTagName) => {
          columnIds.push(colTagName)
          columnNames.push(colTagName)
        })
      } else {
        columnIds.push(DUMMY_COLUMN_ID)
        columnNames.push(messageUtil.viewOption(widgetSetting.viewOption!))
      }
      break
    case ComparisonMode.DOC_HIERARCHIES:
      if (colDocNames && colDocNames.length > 0) {
        if (sortAlphabetically || (secondarySort && !displayColumn)) {
          colDocNames.sort(sort)
        }
        colDocNames.forEach((colDocName) => {
          columnIds.push(colDocName)
          columnNames.push(colDocName)
        })
      } else {
        columnIds.push(DUMMY_COLUMN_ID)
        columnNames.push(messageUtil.viewOption(widgetSetting.viewOption!))
      }
      break
    default:
      break
  }
  // Ning's TODO: suggest using column[{name:xxx, id: yyy}]
  if (showTable && displayColumn) {
    columnNames.push(displayAverage ? i18n.t('average') : i18n.t('total'))
  }

  if (displayRowGrandTotal) {
    columnIds.push(ROW_GRAND_TOTAL_ID)
    if (isAudienceAnalysis) {
      columnNames.push(i18n.t('audienceTotal'))
    } else {
      columnNames.push(i18n.t('topicTotal'))
    }
  }

  return { columnIds, columnNames }
}

export const getPivotThemeIds = (widgetSetting: CrosstabWidgetsType) => {
  let pivotThemeIds: number[] = []
  if (widgetSetting.pivotThemeInfos && widgetSetting.pivotThemeInfos.length > 0) {
    pivotThemeIds = widgetSetting.pivotThemeInfos.map((pivotThemeInfo) => pivotThemeInfo.themeId!)
  }
  return pivotThemeIds
}

export const getPivotRowThemeIds = (widgetSetting: CrosstabWidgetsType) => {
  let pivotRowThemeIds: number[] = []
  if (widgetSetting.pivotRowThemeInfos && widgetSetting.pivotRowThemeInfos.length > 0) {
    pivotRowThemeIds = widgetSetting.pivotRowThemeInfos.map(
      (pivotRowThemeInfo) => pivotRowThemeInfo.themeId!
    )
  }
  return pivotRowThemeIds
}

const getColComparisonMode = (widgetSetting: CrosstabWidgetsType) => {
  if (
    widgetSetting.colComparisonMode === ComparisonMode.THEMES &&
    !isEmpty(getPivotThemeIds(widgetSetting))
  ) {
    return ComparisonMode.THEMES
  }

  if (widgetSetting.colComparisonMode === ComparisonMode.TAGS && !isEmpty(widgetSetting.tagNames)) {
    return ComparisonMode.TAGS
  }

  if (
    widgetSetting.colComparisonMode === ComparisonMode.DOC_HIERARCHIES &&
    !isEmpty(widgetSetting.docNames)
  ) {
    return ComparisonMode.DOC_HIERARCHIES
  }
  return ComparisonMode.NONE
}

const getRowComparisonMode = (widgetSetting: CrosstabWidgetsType) => {
  if (
    widgetSetting.rowComparisonMode === ComparisonMode.THEMES &&
    !isEmpty(getPivotRowThemeIds(widgetSetting))
  ) {
    return ComparisonMode.THEMES
  }
  if (
    widgetSetting.rowComparisonMode === ComparisonMode.TAGS &&
    !isEmpty(widgetSetting.rowTagNames)
  ) {
    return ComparisonMode.TAGS
  }
  if (
    widgetSetting.rowComparisonMode === ComparisonMode.DOC_HIERARCHIES &&
    !isEmpty(widgetSetting.rowDocNames)
  ) {
    return ComparisonMode.DOC_HIERARCHIES
  }
  return ComparisonMode.TOPICS
}

export const getComparisonMode = (widgetSetting: CrosstabWidgetsType) => ({
  colComparisonMode: getColComparisonMode(widgetSetting),
  rowComparisonMode: getRowComparisonMode(widgetSetting)
})
